import React from 'react';
import { motion } from 'framer-motion';
import { 
    Rocket, 
    Target, 
    Users, 
    Shield, 
    Globe, 
    Brain, 
    ChevronRight,
    Award,
    Zap
} from 'lucide-react';
import './AboutPage.css';

const values = [
    {
        icon: Brain,
        title: "Innovation First",
        description: "We push the boundaries of AI technology to solve real-world problems."
    },
    {
        icon: Shield,
        title: "Trust & Security",
        description: "We maintain the highest standards of data security and privacy."
    },
    {
        icon: Users,
        title: "Customer Success",
        description: "Your success is our success. We're committed to your growth."
    },
    {
        icon: Globe,
        title: "Global Impact",
        description: "We're building solutions that make a difference worldwide."
    }
];

const milestones = [
    {
        year: "Q2 2023",
        title: "Concept inception",
        description: "Vision established for an intelligent knowledge platform for construction and infrastructure."
    },
    {
        year: "Sep 2023",
        title: "First prototype",
        description: "Built initial search capabilities with document processing and intelligent retrieval."
    },
    {
        year: "Jan 2024",
        title: "Beta launch",
        description: "Opened closed beta program with select industry partners to validate core functionality."
    },
    {
        year: "Jul 2024",
        title: "Enhanced intelligence",
        description: "Added advanced relationship mapping to connect data across projects and systems."
    },
    {
        year: "Sep 2024",
        title: "Platform refinement",
        description: "Major user experience improvements based on customer feedback and usage patterns."
    },
    {
        year: "Nov 2024",
        title: "BidAI development",
        description: "Began development of AI-powered proposal and bid response generation capabilities."
    },
    {
        year: "Dec 2024",
        title: "Production readiness",
        description: "Prepared infrastructure and systems for enterprise-scale deployment."
    },
    {
        year: "Apr 2025",
        title: "Advanced capabilities",
        description: "Launched enhanced AI features and strengthened enterprise security framework."
    },
    {
        year: "Apr 2025",
        title: "Performance optimization",
        description: "Implemented scalability improvements to support growing user base and data volumes."
    },
    {
        year: "May 2025",
        title: "Brand evolution",
        description: "Refreshed market presence with updated messaging and interactive product experiences."
    }
];

const AboutPage = () => {
    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0,
            transition: {
                type: "spring",
                stiffness: 50
            }
        }
    };

    return (
        <div className="about-page">
            {/* Hero Section */}
            <motion.section 
                className="about-hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6 }}
            >
                <div className="glass-effect hero-glass"></div>
                <motion.h1
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    Transforming Knowledge Management with <span className="gradient-text">AI</span>
                </motion.h1>
                <motion.p className="hero-description">
                    Ufficient is building the future of enterprise intelligence, helping organizations unlock the full potential of their knowledge.
                </motion.p>
            </motion.section>

            {/* Mission Section */}
            <section className="mission-section">
                <div className="container">
                    <motion.div 
                        className="mission-content"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <div className="mission-text">
                            <h2>Our Mission</h2>
                            <p>To empower organizations with AI-driven knowledge management solutions that enhance decision-making, accelerate innovation, and drive success in the digital age.</p>
                        </div>
                        <div className="mission-visual">
                            <div className="glass-effect mission-glass"></div>
                            <Target className="mission-icon" />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Values Section */}
            <section className="values-section">
                <div className="container">
                    <h2>Our Values</h2>
                    <motion.div 
                        className="values-grid"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        {values.map((value, index) => (
                            <motion.div 
                                key={index}
                                className="value-card"
                                variants={itemVariants}
                                whileHover={{ 
                                    y: -8, 
                                    boxShadow: "0 10px 25px rgba(0, 120, 255, 0.15)",
                                    transition: { duration: 0.2 } 
                                }}
                            >
                                <div className="value-icon-container">
                                    <value.icon className="value-icon" />
                                </div>
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Journey Section */}
            <section className="journey-section">
                <div className="container">
                    <h2>Our Journey</h2>
                    <div className="timeline">
                        {milestones.map((milestone, index) => (
                            <motion.div 
                                key={index}
                                className="timeline-item"
                                initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <div className="timeline-marker"></div>
                                <div className="timeline-content">
                                    <span className="year">{milestone.year}</span>
                                    <h3>{milestone.title}</h3>
                                    <p>{milestone.description}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="about-cta">
                <div className="container">
                    <motion.div 
                        className="cta-content"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                    >
                        <h2>Join Us on Our Mission</h2>
                        <p>Experience how Ufficient can transform your organization's knowledge management.</p>
                        {/* Deprecated demo and careers links removed */}
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default AboutPage;